import { useMutation, useQuery } from "@apollo/client/react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import { gql } from "~/__generated__"
import { JourneyLogDisplay } from "~/__generated__/graphql"
import { useViewer } from "~/auth/use-viewer"
import { directImageUpload } from "~/common/direct-image-upload"
import { userPublicProfilePath } from "~/common/paths"
import { useDocumentTitle } from "~/common/use-document-title"
import { Card, CardContent } from "~/ui/card"
import { GraphqlError } from "~/ui/graphql-error"
import { ScreenLoading } from "~/ui/screen-loading"
import { JourneyLogForm } from "./journey-log-form"

const journeyLogQuery = gql(/* GraphQL */ `
  query JourneyLogForEdit($id: ID!) {
    node(id: $id) {
      ... on JourneyLog {
        id
        completedOn
        city
        state
        zipCode
        comment
        passedOn
        nextLocation
        displayAs
        photoUrl
        viewerIsAuthor
        puzzle {
          id
          wpId
          title
          brand
          pieceCount
          photoThumbUrl
        }
      }
    }
  }
`)

const journeyLogUpdateMutation = gql(/* GraphQL */ `
  mutation JourneyLogUpdate($input: JourneyLogUpdateInput!) {
    journeyLogUpdate(input: $input) {
      journeyLog {
        id
      }
    }
  }
`)

export const JourneyLogEditScreen = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { viewer } = useViewer()

  const { data, loading, error } = useQuery(journeyLogQuery, {
    variables: { id: id ?? "" },
    skip: !id,
  })

  const log = data?.node?.__typename === "JourneyLog" ? data.node : null
  useDocumentTitle(log ? `Edit your log · ${log.puzzle.title}` : null)

  const [exec, { loading: submitting }] = useMutation(journeyLogUpdateMutation)

  if (loading) return <ScreenLoading />
  if (error) return <GraphqlError error={error} />
  if (!log || !id) {
    return (
      <main className="mx-auto max-w-2xl px-4 py-12">
        <h1 className="text-2xl font-semibold">Log not found</h1>
        <Link to="/" className="mt-4 inline-block text-blue-600 underline">
          Back to home
        </Link>
      </main>
    )
  }

  if (!log.viewerIsAuthor) {
    return (
      <main className="mx-auto max-w-2xl px-4 py-12">
        <h1 className="text-2xl font-semibold">You can&apos;t edit this log</h1>
        <p className="mt-2 text-gray-600">Only the author can edit a journey log.</p>
        <Link to={`/p/${log.puzzle.wpId}`} className="mt-6 inline-block text-blue-600 underline">
          Back to puzzle
        </Link>
      </main>
    )
  }

  const backToProfile = userPublicProfilePath({ userId: viewer.id })

  return (
    <main className="mx-auto max-w-2xl px-4 py-6 sm:py-10">
      <Link to={backToProfile} className="text-sm text-gray-500 hover:text-gray-700">
        ← Back to your profile
      </Link>

      <Card className="mt-4">
        <CardContent className="flex items-center gap-4 py-4">
          {log.puzzle.photoThumbUrl ? (
            <img
              src={log.puzzle.photoThumbUrl}
              alt={log.puzzle.title}
              className="h-16 w-16 rounded bg-stone-100 object-contain"
            />
          ) : null}
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">
              Editing your log on
            </div>
            <div className="font-medium">{log.puzzle.title}</div>
            <div className="text-sm text-gray-500">
              {log.puzzle.brand} · {log.puzzle.pieceCount.toLocaleString()} pieces
            </div>
          </div>
        </CardContent>
      </Card>

      <JourneyLogForm
        defaultValues={{
          completedOn: log.completedOn,
          city: log.city ?? "",
          state: log.state ?? "",
          zipCode: log.zipCode ?? "",
          comment: log.comment ?? "",
          passedOn: log.passedOn,
          nextLocation: log.nextLocation ?? "",
          displayAs: log.displayAs,
        }}
        initialPhotoUrl={log.photoUrl ?? null}
        firstName={viewer.firstName}
        submitLabel="Save changes"
        submittingLabel="Saving…"
        submitting={submitting}
        cancelTo={backToProfile}
        onSubmit={async ({ values, photoFile, removeExistingPhoto }) => {
          let photoSignedId: string | undefined
          if (photoFile) {
            try {
              const upload = await directImageUpload(photoFile)
              photoSignedId = upload.signedId
            } catch {
              toast.error("Photo upload failed", {
                description: "Try a smaller image or skip the photo.",
              })
              return
            }
          }

          const result = await exec({
            variables: {
              input: {
                id,
                journeyLogInput: {
                  completedOn: values.completedOn,
                  city: values.city?.trim() || null,
                  state: values.state?.trim() || null,
                  zipCode: values.zipCode?.trim() || null,
                  comment: values.comment?.trim() || null,
                  passedOn: values.passedOn,
                  nextLocation: values.passedOn ? values.nextLocation?.trim() || null : null,
                  displayAs: values.displayAs as JourneyLogDisplay,
                },
                photoSignedId,
                removePhoto: !photoSignedId && removeExistingPhoto,
              },
            },
          })

          if (result.error) {
            toast.error("Could not save your changes", {
              description: "Please check the form and try again.",
            })
            return
          }

          toast.success("Saved!", { description: "Your log has been updated." })
          navigate(backToProfile)
        }}
      />
    </main>
  )
}
